/* eslint-disable complexity */

import {
    useState,
    useEffect,
    useContext,
    createContext,
    useMemo,
    PropsWithChildren
} from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Adg4Skeleton } from "@/components/adg4/skeleton/src/skeleton";
import { ProductKey } from "@/components/signup/utils/products";
import { Picture } from "@/components/wpl/image/src/image";
import { Image } from "@/libraries/design/components";
import {
    BanditsContent,
    nullBanditsContent,
    JIRA_FEATURES_ASSET_CONTROL,
    CONFLUENCE_FEATURES_ASSET_CONTROL,
    isConfluenceSubAnchorAssetControl,
    BANDITS_USE_CASE,
    BanditsUseCase,
    BanditsProduct
} from "@/utils/anchor-queries/get-bandits-contents";
import {
    getGrsAndContentfulContent,
    setGrsDataToLocalStorage
} from "@/utils/anchor-queries/get-bandits-contents";
import { Cookie } from "@/utils/cookie";

// Bandits Provider and context hook

export type BanditsContextType = {
    banditsContent: BanditsContent | null;
    loading: boolean;
    error: Error | null;
    isExperimentEnabled: boolean;
};

const BanditsContext = createContext<BanditsContextType>({
    banditsContent: null,
    loading: false,
    error: null,
    isExperimentEnabled: false
});

export const BanditsProvider = ({
    product,
    keyword,
    isExperimentEnabled = false,
    useCase = BANDITS_USE_CASE.ANCHOR_JOURNEY,
    subAnchor = "",
    channel = "",
    utmContent = "",
    utmCampaign = "",
    children
}: {
    product: BanditsProduct;
    keyword?: string;
    isExperimentEnabled: boolean;
    useCase?: BanditsUseCase;
    subAnchor?: string;
    channel?: string;
    utmContent?: string;
    utmCampaign?: string;
} & PropsWithChildren) => {
    const anonymousId = decodeURIComponent(
        Cookie.readCookie("ajs_anonymous_id") || ""
    ).replace(/['"]+/g, "");

    const [banditsContent, setBanditsContent] = useState<BanditsContent | null>(
        isExperimentEnabled ? null : nullBanditsContent
    );
    const [loading, setLoading] = useState<boolean>(isExperimentEnabled);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!isExperimentEnabled) {
            return;
        }
        const getBanditsContent = async () => {
            const content = await getGrsAndContentfulContent({
                keyword: keyword || "",
                product,
                locale: "en-US",
                anonymousId,
                useCase,
                subAnchor,
                channel,
                utmContent,
                utmCampaign
            });

            if (content?.recoSessionId) {
                // Map BanditsProduct to a productKey string for storage
                const productKey =
                    product === "jira"
                        ? ProductKey.JIRA_SOFTWARE
                        : ProductKey.CONFLUENCE;

                setGrsDataToLocalStorage(
                    {
                        recoSessionId: content.recoSessionId,
                        recoEntityId: content.recoEntityId
                    },
                    productKey
                );
            }
            setBanditsContent(content);
        };

        try {
            getBanditsContent();
        } catch (e) {
            setError(e as Error);
        } finally {
            setLoading(false);
        }
    }, [
        isExperimentEnabled,
        keyword,
        product,
        anonymousId,
        useCase,
        subAnchor,
        channel,
        utmContent,
        utmCampaign
    ]);

    const value = useMemo(
        () => ({
            banditsContent,
            loading,
            error,
            isExperimentEnabled
        }),
        [banditsContent, loading, error, isExperimentEnabled]
    );

    return (
        <BanditsContext.Provider value={value}>
            {children}
        </BanditsContext.Provider>
    );
};

export const useBanditsContext = (): BanditsContextType => {
    const context = useContext(BanditsContext);
    if (!context) {
        console.info("useBanditsContext is not used within a BanditsProvider");
    }
    return context;
};

// Wrapper Components

const imgContainer = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    maxHeight: "650px",
    width: "100%",
    maxWidth: "56%"
});

const confluenceImgContainer = css({
    minWidth: 280,
    maxWidth: "100%",
    flex: "1 1 100%",
    [media.below.sm]: {
        display: "none"
    }
});

export const BanditsHeaderWrapper = ({
    product,
    type = "header",
    children
}: {
    product: BanditsProduct;
    type: "header" | "subheader";
} & PropsWithChildren) => {
    const { banditsContent, loading, error, isExperimentEnabled } =
        useBanditsContext();

    const SkeletonComponent =
        product === "jira"
            ? JiraHeroHeaderSkeleton
            : ConfluenceHeroHeaderSkeleton;

    if (loading || (!banditsContent && isExperimentEnabled)) {
        return <SkeletonComponent type={type} />;
    }

    if (error || !banditsContent?.headline) {
        return children;
    }

    return (
        <>
            {type === "header"
                ? banditsContent?.headline?.title
                : banditsContent?.headline?.subheading}
        </>
    );
};

export const BanditsAssetWrapper = ({
    product,
    showChildren = false,
    children
}: {
    product: BanditsProduct;
    showChildren: boolean;
} & PropsWithChildren) => {
    const { banditsContent, loading, error, isExperimentEnabled } =
        useBanditsContext();

    if (loading || (!banditsContent && isExperimentEnabled)) {
        return (
            <div
                css={[
                    css({
                        width: "100%",
                        maxWidth: "700px",
                        justifyContent: "center",
                        display: "none",
                        [media.above.sm]: { display: "flex" }
                    })
                ]}
            >
                <HeroAssetSkeleton />
            </div>
        );
    }

    if (
        showChildren ||
        error ||
        !banditsContent?.asset ||
        banditsContent?.asset?.id === JIRA_FEATURES_ASSET_CONTROL ||
        banditsContent?.asset?.id === CONFLUENCE_FEATURES_ASSET_CONTROL ||
        isConfluenceSubAnchorAssetControl(banditsContent?.asset?.id)
    ) {
        return children;
    }

    const image1OrangeLogic = banditsContent?.asset?.image1OrangeLogic;

    return (
        <div
            css={[
                imgContainer,
                product === "confluence" && confluenceImgContainer,
                image1OrangeLogic && css({ width: "auto", flex: "0 1 auto" })
            ]}
        >
            {banditsContent?.asset?.video1Mp4?.url ? (
                <video
                    css={css({ maxHeight: "700px", maxWidth: "100%" })}
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    preload="auto"
                    poster={banditsContent?.asset?.image1?.url}
                    controls={true}
                >
                    <source
                        src={banditsContent?.asset?.video1Mp4?.url}
                        type="video/mp4"
                    />
                    <source
                        src={banditsContent?.asset?.video1Webm?.url}
                        type="video/webm"
                    />
                </video>
            ) : image1OrangeLogic ? (
                <div css={css({ maxWidth: "700px", maxHeight: "600px" })}>
                    <Image
                        src={image1OrangeLogic.assetUrl || ""}
                        alt={image1OrangeLogic.alt ?? ""}
                    />
                </div>
            ) : (
                <Picture
                    image={{
                        src: banditsContent?.asset?.image1?.url || "",
                        alt: banditsContent?.asset?.image1?.description || ""
                    }}
                    css={css({ maxHeight: "700px" })}
                />
            )}
        </div>
    );
};

// Skeleton Components

const sectionGap = css({
    marginBlockEnd: token("space.100")
});

const desktopDisplay = css({
    display: "none",
    [media.above.md]: {
        display: "block"
    }
});

const tabletDisplay = css({
    display: "none",
    [media.only.sm]: {
        display: "block"
    }
});

const mobileDisplay = css({
    display: "none",
    [media.below.sm]: {
        display: "block"
    }
});

const containerStyles = css({
    display: "flex",
    flexDirection: "column",
    [media.below.md]: {
        alignItems: "center"
    }
});

export const JiraHeroHeaderSkeleton = ({
    type
}: {
    type: "header" | "subheader";
}) => {
    if (type === "subheader") {
        return;
    }

    return (
        <div data-testid="hero-header-skeleton">
            <div css={desktopDisplay}>
                <div css={containerStyles}>
                    <div css={[sectionGap]}>
                        <Adg4Skeleton width="420px" height="64px" />
                    </div>
                    <div css={sectionGap}>
                        <Adg4Skeleton width="360px" height="64px" />
                    </div>
                </div>
            </div>
            <div css={tabletDisplay}>
                <div css={containerStyles}>
                    <div css={[sectionGap]}>
                        <Adg4Skeleton width="680px" height="64px" />
                    </div>
                    <div css={[sectionGap]}>
                        <Adg4Skeleton width="460px" height="64px" />
                    </div>
                </div>
            </div>
            <div css={mobileDisplay}>
                <div css={containerStyles}>
                    <div css={[sectionGap]}>
                        <Adg4Skeleton width="320px" height="64px" />
                    </div>
                    <div css={[sectionGap]}>
                        <Adg4Skeleton width="280px" height="64px" />
                    </div>
                    <div css={[sectionGap]}>
                        <Adg4Skeleton width="240px" height="64px" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ConfluenceHeroHeaderSkeleton = ({
    type
}: {
    type: "header" | "subheader";
}) => {
    if (type === "subheader") {
        return;
    }

    return (
        <div>
            <div css={sectionGap}>
                <Adg4Skeleton width="280px" height="32px" />
            </div>
            <div css={sectionGap}>
                <Adg4Skeleton width="300px" height="32px" />
            </div>
            <div css={sectionGap}>
                <Adg4Skeleton width="320px" height="32px" />
            </div>
        </div>
    );
};

export const HeroAssetSkeleton = () => {
    return (
        <div data-testid="hero-asset-skeleton">
            <div css={desktopDisplay}>
                <Adg4Skeleton width="600px" height="480px" />
            </div>
            <div css={tabletDisplay}>
                <Adg4Skeleton width="420px" height="280px" />
            </div>
            <div css={mobileDisplay}>
                <Adg4Skeleton width="250px" height="200px" />
            </div>
        </div>
    );
};
